import React, { FC, useCallback } from 'react';
import { PostModel } from '../../../data-layer/posts/types';
import { VirtualFeed, VirtualFeedProps } from '../../../ui/layout/VirtualFeed/VirtualFeed';
import { PostAdCard } from '../../../ui/posts/PostCard/PostAdCard';
import { calculateHeight, PostCardWithVisibilityCheck } from '../../../ui/posts/PostCard/PostCard';
import { PostContextMenu } from '../../../ui/posts/PostContextMenu';
import { EventSource } from '../../../utils/analyticsService';
import { getWindow } from '../../../utils/getWindow';
import { useBodyScrollBlock } from '../../../utils/useBodyScrollBlock';
import { useCurrentUser } from '../../users/useCurrentUser';
import { WithLoadMore } from '../../withLoadMore';
import { withPostCallbacks } from '../withPostCallbacks';
import { PostModelWithAd, useItemsWithAd } from './hooks/useItemsWithAd';

type FeedSnapScrollProps = {
    items: PostModel[];
    onLoadNext: VoidFunction;
    heightOffset?: number;
};

const MOBILE_FOOTER_HEIGHT = 56;
const AD_INTERVAL = 5;
const AD_AD_TOP_FEED_OFFSET = 3;
const LOAD_MORE_INTERVAL = 10;

const EnhancedPostCard = withPostCallbacks(PostCardWithVisibilityCheck, EventSource.FEED);
const EnhancedPostMenu = withPostCallbacks(PostContextMenu, EventSource.FEED);

export const FeedSnapScroll: FC<FeedSnapScrollProps> = ({
    items,
    onLoadNext,
    heightOffset = 0,
}) => {
    const itemsWithAd = useItemsWithAd({
        items,
        adInterval: AD_INTERVAL,
        adTopFeedOffset: AD_AD_TOP_FEED_OFFSET,
    });
    const currentUser = useCurrentUser();
    useBodyScrollBlock();

    const containerHeight = getWindow()?.innerHeight! - heightOffset;

    const getPost = useCallback(
        (post: PostModelWithAd, index: number, isCardActive?: boolean) =>
            post.isAd ? (
                <PostAdCard
                    postIndex={index}
                    isActiveCard={isCardActive}
                    adInterval={AD_INTERVAL}
                />
            ) : (
                <EnhancedPostCard
                    post={post}
                    index={index}
                    maxHeightOffset={MOBILE_FOOTER_HEIGHT}
                    isCardActive={isCardActive}
                    renderMenu={(trigger) => (
                        <EnhancedPostMenu
                            trigger={trigger}
                            post={post}
                            user={currentUser}
                            index={index}
                        />
                    )}
                />
            ),
        [],
    );

    const getPostWithLoadMore: VirtualFeedProps<PostModelWithAd>['renderItem'] = useCallback(
        ({ item, index, isActive }) => {
            if (index !== 0 && index % LOAD_MORE_INTERVAL === 0) {
                return (
                    <WithLoadMore onLoadMore={onLoadNext} height="100%">
                        {getPost(item, index, isActive)}
                    </WithLoadMore>
                );
            }

            return getPost(item, index, isActive);
        },
        [getPost, onLoadNext],
    );

    return (
        <VirtualFeed<PostModelWithAd>
            items={itemsWithAd}
            containerHeight={containerHeight}
            renderItem={getPostWithLoadMore}
            containerStyles={{
                width: '100%',
            }}
            getHeight={(postOrAd) =>
                postOrAd.isAd
                    ? containerHeight
                    : calculateHeight(window.innerWidth, postOrAd.content)
            }
        />
    );
};
